import { useViewModel } from "mvvm/View";
import { Popover, Select } from "antd";
import { TagOutlined } from "@ant-design/icons";
import { ParentWrapper } from "styles/global";
import { useEffect, useRef, useState } from "react";
import { useDarkMode, useThemeColors } from "redux/hooks";
import { ConditionalTooltip } from "./ConditionalTooltip";
import { ViewModel } from "mvvm/ViewModel";

// TagViewModel interface - compatible with both RunDash and RunNLI TagViewModel classes
interface ITagViewModel extends ViewModel {
    tags: string[];
    isSaving: boolean;
    isOpen: boolean;
    update: () => void;
    job: {
        tags?: string[];
    };
}

export function TagView(props: { model: ITagViewModel, tagList: string[], onOpenChange?: (isOpen: boolean) => void }) {

    const vm = useViewModel(props.model) as ITagViewModel;
    const darkMode = useDarkMode();
    const themeColors = useThemeColors();

    const selectRef = useRef<any>(null);
    const [isHovered, setIsHovered] = useState(false);
    const [isPressed, setIsPressed] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const setFocus = () => {
        setTimeout(() => {
            selectRef.current.focus();
        }, 200);
    }

    useEffect(() => {
        if (isOpen) {
            setFocus();
        }
    }, [isOpen]);

    return (
        <>
            <style>{`
                .tag-view-select-dropdown .ant-select-item {
                    padding: 1px 6px !important;
                }
                .tag-view-select-dropdown {
                    background-color: ${themeColors.backgroundColorSecondary} !important;
                    border: 1px solid ${themeColors.borderColor} !important;
                    box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.48) !important;
                }
                .tag-view-select-dropdown .ant-select-item {
                    color: ${themeColors.textColor} !important;
                }
                .tag-view-select-dropdown .ant-select-item:hover {
                    background-color: ${themeColors.hoverBackground} !important;
                }
                .tag-view-select-dropdown .ant-select-item-option-selected {
                    background-color: ${themeColors.selectedBackground} !important;
                }
                .tag-view-select-dropdown .ant-select-item-option-disabled {
                    color: ${themeColors.textColorMuted} !important;
                }
                .tag-view-select .ant-select-selector {
                    background-color: ${themeColors.backgroundColorSecondary} !important;
                    border-color: ${themeColors.borderColor} !important;
                    color: ${themeColors.textColor} !important;
                }
                .tag-view-select .ant-select-selection-item {
                    background-color: ${themeColors.backgroundColor} !important;
                    border-color: ${themeColors.borderColor} !important;
                    color: ${themeColors.textColor} !important;
                }
                .tag-view-select .ant-select-selection-placeholder {
                    color: ${themeColors.textColorMuted} !important;
                }
                .tag-view-select .ant-select-arrow {
                    color: ${themeColors.textColor} !important;
                }
                .tag-view-select .ant-select-clear {
                    color: ${themeColors.textColor} !important;
                    background-color: transparent !important;
                }
                .tag-view-select .ant-select-clear:hover {
                    color: ${themeColors.textColorSecondary} !important;
                }
                .tag-view-select .ant-select-selection-item-remove {
                    color: ${themeColors.textColor} !important;
                }
                .tag-view-select .ant-select-selection-item-remove:hover {
                    color: ${themeColors.textColorSecondary} !important;
                }
                .tag-view-select-dropdown .ant-select-item-empty {
                    color: ${themeColors.textColor} !important;
                }
                .tag-view-select-dropdown .ant-empty {
                    color: ${themeColors.textColor} !important;
                }
                .tag-view-select-dropdown .ant-empty-description {
                    color: ${themeColors.textColorMuted} !important;
                }
            `}</style>
            <Popover
                trigger="click"
                placement="right"
                onOpenChange={open => {
                    setIsOpen(open);
                    vm.isOpen = open;
                    props.onOpenChange?.(open);
                }}
                open={isOpen}
                overlayInnerStyle={{
                    width:"30em",
                    padding: "0",
                    marginRight: "-0.5em",
                    backgroundColor: themeColors.backgroundColorSecondary,
                    border: `1px solid ${themeColors.borderColor}`
                }}
                content={
                    <ParentWrapper>
                        <div className="flex-column text-content hugging">
                            <Select
                            mode="tags"
                            ref={selectRef}
                            autoFocus={true}
                            className={"flex-1 tag-view-select"}
                            showAction={['focus', 'click']}
                            style={{
                                resize: "none",
                                fontSize: "1rem",
                                zIndex: 1000,
                                backgroundColor: themeColors.backgroundColorSecondary,
                                color: themeColors.textColor
                            }}
                            placeholder="Enter or select a tag..."
                            size="small"
                            allowClear={true}
                            maxTagCount='responsive'
                            popupClassName="tag-view-select-dropdown"
                            value={vm.tags.filter(e => e !== "").map(e => e.trim())}
                            onChange={e => {
                                if (!vm.isSaving) {
                                    vm.tags = Array.from(new Set(e.map(tag => tag.trim().toUpperCase()).filter(e => e !== "")));
                                    vm.update();
                                }
                            }}
                            onInputKeyDown={e => {
                                if (e.key === 'Tab') {
                                    e.preventDefault();
                                }
                                if (e.key === 'Enter' && e.currentTarget.value === "") {
                                    const oldTags = vm.tags;
                                    setTimeout(() => {
                                        vm.tags = oldTags;
                                    }, 0);
                                }
                            }}
                        >
                            {props.tagList.sort().map(tag => (
                                <Select.Option
                                    key={tag}
                                    value={tag}
                                    style={vm.isSaving
                                        ? {color: themeColors.textColorMuted, cursor: "default"}
                                        : {}}
                                >
                                    {tag}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                </ParentWrapper>
            }
        >
            <div style={{ display: "inline-block" }}>
                <ConditionalTooltip title={vm.job.tags && vm.job.tags.length > 0 ? vm.job.tags.join(", ") : "Add tags"}>
                    <button
                        onClick={() => {
                            setIsPressed(true);
                            setTimeout(() => setIsPressed(false), 200);
                        }}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                        style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "20px",
                            color: (isHovered || isOpen)
                                ? (darkMode ? themeColors.textColor : "#000000")
                                : (darkMode ? "#888888" : "#AFAFAF"),
                            display: "flex",
                            alignItems: "center",
                            padding: "0",
                            transition: "color 0.2s, transform 0.1s",
                            transform: isPressed ? "scale(0.85)" : "scale(1)"
                        }}
                    >
                        <TagOutlined />
                    </button>
                </ConditionalTooltip>
            </div>
        </Popover>
        </>
    );
}

